import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  Boxes, Search, Plus, Pencil, Trash2, Download, ArrowUp, ArrowDown,
  RotateCcw, AlertTriangle, History, Settings2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const STATUS_STYLE = {
  OK: "bg-[#DAE6D7] text-[#3F6B3C] border-[#BCD3B7]",
  BAJO: "bg-[#FDF3E1] text-[#A6751F] border-[#F0DEB2]",
  CRITICO: "bg-[#F4D6D6] text-[#8E2E2E] border-[#E8B5B5]",
};

const TIPO_LABEL = {
  ENTRADA: "Entrada", SALIDA: "Salida", AJUSTE: "Ajuste",
  MERMA: "Merma", PRODUCCION: "Producción", DEVOLUCION: "Devolución",
};

const TIPO_COLOR = {
  ENTRADA: "text-[#3F6B3C]",
  PRODUCCION: "text-[#3F6B3C]",
  DEVOLUCION: "text-[#3F6B3C]",
  SALIDA: "text-[#8E2E2E]",
  MERMA: "text-[#8E2E2E]",
  AJUSTE: "text-[#A6751F]",
};

export default function Stock() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("__all__");
  const [importing, setImporting] = useState(false);

  // create/edit
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    nombre: "", categoria: "", unidad: "u", stock_actual: 0,
    stock_minimo: 0, nota: "", activo: true,
  });

  // movement modal
  const [mvOpen, setMvOpen] = useState(false);
  const [mvItem, setMvItem] = useState(null);
  const [mvForm, setMvForm] = useState({ tipo: "ENTRADA", cantidad: 1, motivo: "" });

  // history modal
  const [histOpen, setHistOpen] = useState(false);
  const [histItem, setHistItem] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const { user } = useAuth();
  const canManageItems = user?.role === "admin" || user?.role === "encargado";
  const canDelete = user?.role === "admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (status !== "__all__") params.status = status;
      const r = await api.get("/stock/items", { params });
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  }, [q, status]);

  useEffect(() => { load(); }, [load]);

  const summary = useMemo(() => {
    const counts = { total: items.length, OK: 0, BAJO: 0, CRITICO: 0 };
    for (const it of items) counts[it.status] = (counts[it.status] || 0) + 1;
    return counts;
  }, [items]);

  const importStock = async () => {
    setImporting(true);
    try {
      const r = await api.post("/stock/items/import-from-productos");
      if (r.data.created > 0) toast.success(`Importé ${r.data.created} item${r.data.created === 1 ? "" : "s"} del catálogo`);
      else toast.info("Ya está todo importado");
      load();
    } catch {
      toast.error("Error al importar");
    } finally {
      setImporting(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm({ nombre: "", categoria: "", unidad: "u", stock_actual: 0, stock_minimo: 0, nota: "", activo: true });
    setDialogOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      nombre: it.nombre, categoria: it.categoria || "", unidad: it.unidad,
      stock_actual: it.stock_actual, stock_minimo: it.stock_minimo,
      nota: it.nota || "", activo: it.activo,
    });
    setDialogOpen(true);
  };
  const save = async () => {
    if (!form.nombre.trim()) { toast.error("Nombre obligatorio"); return; }
    try {
      const payload = {
        nombre: form.nombre.trim(),
        categoria: form.categoria,
        unidad: form.unidad,
        stock_minimo: Number(form.stock_minimo) || 0,
        nota: form.nota,
        activo: form.activo,
      };
      if (editing) {
        await api.put(`/stock/items/${editing.id}`, payload);
        toast.success("Stock actualizado");
      } else {
        await api.post("/stock/items", { ...payload, stock_actual: Number(form.stock_actual) || 0 });
        toast.success("Stock creado");
      }
      setDialogOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    }
  };
  const remove = async (it) => {
    try {
      await api.delete(`/stock/items/${it.id}`);
      toast.success("Stock eliminado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    }
  };

  const openMovement = (it, tipo = "ENTRADA") => {
    setMvItem(it);
    setMvForm({ tipo, cantidad: 1, motivo: "" });
    setMvOpen(true);
  };
  const saveMovement = async () => {
    const c = Number(mvForm.cantidad);
    if (!c || (mvForm.tipo !== "AJUSTE" && c <= 0)) {
      toast.error("Cantidad inválida");
      return;
    }
    try {
      await api.post("/stock/movements", {
        stock_item_id: mvItem.id,
        tipo: mvForm.tipo,
        cantidad: c,
        motivo: mvForm.motivo,
      });
      toast.success(`${TIPO_LABEL[mvForm.tipo]} registrada`);
      setMvOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al registrar movimiento");
    }
  };

  const openHistory = async (it) => {
    setHistItem(it);
    setHistOpen(true);
    setLoadingHistory(true);
    try {
      const r = await api.get(`/stock/movements`, { params: { stock_item_id: it.id, limit: 200 } });
      setHistory(r.data);
    } finally { setLoadingHistory(false); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Inventario"
        title="Stock"
        subtitle="Lleva el control de cantidades. No se bloquean ventas — solo informa."
      >
        {canManageItems && (
          <>
            <Button
              variant="outline"
              className="border-[#E5E0D8] gap-2"
              onClick={importStock}
              disabled={importing}
              data-testid="stock-import"
            >
              <Download className="h-4 w-4" />
              <span className="hidden sm:inline">{importing ? "Importando..." : "Importar del catálogo"}</span>
              <span className="sm:hidden">Importar</span>
            </Button>
            <Button
              className="bg-[#C85A32] hover:bg-[#A64A29] text-white gap-2"
              onClick={openNew}
              data-testid="stock-new"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Nuevo</span>
            </Button>
          </>
        )}
      </PageHeader>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <KPI label="Total" value={summary.total} icon={Boxes} color="text-[#C85A32]" />
        <KPI label="OK" value={summary.OK} color="text-[#3F6B3C]" />
        <KPI label="Bajo" value={summary.BAJO} color="text-[#A6751F]" />
        <KPI label="Crítico" value={summary.CRITICO} color="text-[#8E2E2E]" />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8C8C8C]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar..." className="pl-9 bg-white h-10" data-testid="stock-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-10 sm:w-[160px] bg-white" data-testid="stock-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos</SelectItem>
            <SelectItem value="OK">OK</SelectItem>
            <SelectItem value="BAJO">Bajo</SelectItem>
            <SelectItem value="CRITICO">Crítico</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Items grid */}
      <Card className="border-[#E5E0D8] rounded-2xl bg-white overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#8C8C8C]">Cargando...</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <Boxes className="h-10 w-10 mx-auto text-[#C85A32] opacity-50 mb-2" />
            <div className="font-display font-semibold text-base">Sin items</div>
            <p className="text-xs text-[#595959] mt-1">Tocá "Importar del catálogo" para empezar.</p>
          </div>
        ) : (
          <div className="divide-y divide-[#F0EBE0]">
            {items.map((it) => (
              <div key={it.id} className="p-3 sm:p-4 flex items-center justify-between gap-2 hover:bg-[#FBF8F2]" data-testid={`stock-row-${it.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span className="font-display font-semibold text-[#1A1A1A] truncate">{it.nombre}</span>
                    <StatusBadge status={it.status} />
                    {it.categoria && (
                      <span className="text-[10px] uppercase tracking-wider text-[#8C8C8C]">{it.categoria}</span>
                    )}
                  </div>
                  <div className="text-[11px] text-[#8C8C8C] flex flex-wrap gap-x-3">
                    <span>
                      Actual: <span className="font-mono font-semibold text-[#1A1A1A]">{it.stock_actual}</span> {it.unidad}
                    </span>
                    {Number(it.stock_minimo) > 0 && (
                      <span>Mín: <span className="font-mono">{it.stock_minimo}</span></span>
                    )}
                    {it.nota && <span className="italic">· {it.nota}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <IconBtn title="Entrada" onClick={() => openMovement(it, "ENTRADA")} testid={`stock-in-${it.id}`}>
                    <ArrowUp className="h-4 w-4 text-[#3F6B3C]" />
                  </IconBtn>
                  <IconBtn title="Salida" onClick={() => openMovement(it, "SALIDA")} testid={`stock-out-${it.id}`}>
                    <ArrowDown className="h-4 w-4 text-[#8E2E2E]" />
                  </IconBtn>
                  <IconBtn title="Merma" onClick={() => openMovement(it, "MERMA")} testid={`stock-merma-${it.id}`}>
                    <AlertTriangle className="h-4 w-4 text-[#A6751F]" />
                  </IconBtn>
                  <IconBtn title="Ajuste" onClick={() => openMovement(it, "AJUSTE")} testid={`stock-ajuste-${it.id}`}>
                    <Settings2 className="h-4 w-4 text-[#595959]" />
                  </IconBtn>
                  <IconBtn title="Historial" onClick={() => openHistory(it)} testid={`stock-hist-${it.id}`}>
                    <History className="h-4 w-4 text-[#595959]" />
                  </IconBtn>
                  {canManageItems && (
                    <IconBtn title="Editar" onClick={() => openEdit(it)} testid={`stock-edit-${it.id}`}>
                      <Pencil className="h-4 w-4 text-[#595959]" />
                    </IconBtn>
                  )}
                  {canDelete && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button type="button" title="Eliminar" className="h-9 w-9 rounded-md hover:bg-[#FBEBEB] flex items-center justify-center" data-testid={`stock-del-${it.id}`}>
                          <Trash2 className="h-4 w-4 text-[#8E2E2E]" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Eliminar stock</AlertDialogTitle>
                          <AlertDialogDescription>¿Eliminar "{it.nombre}"? Se borra también su historial.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Volver</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(it)} className="bg-[#B23A3A] hover:bg-[#8E2E2E]">Eliminar</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Create/edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar stock" : "Nuevo item de stock"}</DialogTitle>
            <DialogDescription>{editing ? "El stock actual se modifica con movimientos." : "Crea un nuevo item para llevar conteo."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nombre</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="stock-form-nombre" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Categoría</Label>
                <Input value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} placeholder="Carnes, Panes, ..." />
              </div>
              <div>
                <Label>Unidad</Label>
                <Input value={form.unidad} onChange={(e) => setForm({ ...form, unidad: e.target.value })} placeholder="u, kg, g, L..." />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {!editing && (
                <div>
                  <Label>Stock inicial</Label>
                  <Input type="number" value={form.stock_actual} onChange={(e) => setForm({ ...form, stock_actual: e.target.value })} data-testid="stock-form-actual" />
                </div>
              )}
              <div className={editing ? "col-span-2" : ""}>
                <Label>Stock mínimo (alerta)</Label>
                <Input type="number" value={form.stock_minimo} onChange={(e) => setForm({ ...form, stock_minimo: e.target.value })} data-testid="stock-form-min" />
              </div>
            </div>
            <div>
              <Label>Nota</Label>
              <Input value={form.nota} onChange={(e) => setForm({ ...form, nota: e.target.value })} placeholder="opcional" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-[#C85A32] hover:bg-[#A64A29] text-white" data-testid="stock-form-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Movement dialog */}
      <Dialog open={mvOpen} onOpenChange={setMvOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{mvItem ? `${TIPO_LABEL[mvForm.tipo]}: ${mvItem.nombre}` : ""}</DialogTitle>
            <DialogDescription>
              {mvForm.tipo === "AJUSTE"
                ? "Aceptá valores negativos para corregir hacia abajo."
                : "La cantidad siempre en positivo; el tipo define el signo."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Tipo de movimiento</Label>
              <Select value={mvForm.tipo} onValueChange={(v) => setMvForm({ ...mvForm, tipo: v })}>
                <SelectTrigger data-testid="mv-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ENTRADA">Entrada</SelectItem>
                  <SelectItem value="SALIDA">Salida</SelectItem>
                  <SelectItem value="AJUSTE">Ajuste (firmable)</SelectItem>
                  <SelectItem value="MERMA">Merma</SelectItem>
                  <SelectItem value="DEVOLUCION">Devolución</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Cantidad</Label>
              <Input
                type="number"
                step="0.01"
                value={mvForm.cantidad}
                onChange={(e) => setMvForm({ ...mvForm, cantidad: e.target.value })}
                data-testid="mv-cantidad"
              />
            </div>
            <div>
              <Label>Motivo / Nota</Label>
              <Input
                value={mvForm.motivo}
                onChange={(e) => setMvForm({ ...mvForm, motivo: e.target.value })}
                placeholder="opcional"
                data-testid="mv-motivo"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMvOpen(false)}>Cancelar</Button>
            <Button onClick={saveMovement} className="bg-[#C85A32] hover:bg-[#A64A29] text-white" data-testid="mv-save">
              Registrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={histOpen} onOpenChange={setHistOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-[#C85A32]" />
              Historial: {histItem?.nombre}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {loadingHistory ? (
              <div className="text-center text-[#8C8C8C] py-6">Cargando...</div>
            ) : history.length === 0 ? (
              <div className="text-center text-[#8C8C8C] py-6">Sin movimientos.</div>
            ) : (
              <ul className="divide-y divide-[#F0EBE0]">
                {history.map((m) => (
                  <li key={m.id} className="py-2.5 flex items-center justify-between gap-2" data-testid={`hist-${m.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold text-[12px] ${TIPO_COLOR[m.tipo] || ""}`}>{TIPO_LABEL[m.tipo]}</span>
                        <span className="font-mono text-[11px] text-[#8C8C8C]">{m.fecha}</span>
                      </div>
                      <div className="text-[11px] text-[#8C8C8C]">
                        {m.user_name} ({m.role}){m.motivo && <> · {m.motivo}</>}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className={`font-mono font-bold ${m.cantidad_delta >= 0 ? "text-[#3F6B3C]" : "text-[#8E2E2E]"}`}>
                        {m.cantidad_delta >= 0 ? "+" : ""}{m.cantidad_delta} {m.unidad}
                      </div>
                      <div className="text-[10px] text-[#8C8C8C] font-mono">
                        → {m.stock_resultante}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KPI({ label, value, color = "text-[#1A1A1A]", icon: Icon }) {
  return (
    <Card className="p-3 sm:p-4 border-[#E5E0D8] rounded-2xl bg-white">
      <div className="text-[10px] uppercase tracking-wider text-[#8C8C8C] flex items-center gap-1.5">
        {Icon && <Icon className="h-3.5 w-3.5" />}
        {label}
      </div>
      <div className={`font-display font-bold text-2xl sm:text-3xl ${color} mt-1`}>{value}</div>
    </Card>
  );
}

function StatusBadge({ status }) {
  const cls = STATUS_STYLE[status] || STATUS_STYLE.OK;
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-[0.1em] border ${cls}`} data-testid={`stock-status-${status}`}>
      {status}
    </span>
  );
}

function IconBtn({ title, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="h-9 w-9 rounded-md hover:bg-[#F4EAE6] flex items-center justify-center transition-colors"
      data-testid={testid}
    >
      {children}
    </button>
  );
}
